import { useState, type FormEvent } from 'react'
import type { AdminCredentials } from '../../core/index.js'
import { LogIn, ShieldCheck } from 'lucide-react'
import { Button, PasswordInput } from '../../core/index.js'

/** Where a client's operators, facility administrators and workers sign in. */
const CLIENT_SIGN_IN_PATH = '/'

const INPUT_CLASS =
  'w-full border-2 border-marine/25 px-4 py-3.5 mb-5 text-base text-ink focus:outline-none focus:border-coral'

interface AdminLoginProps {
  isVerifying: boolean
  error: string | null
  onSubmit: (credentials: AdminCredentials) => void
}

export function AdminLogin({ isVerifying, error, onSubmit }: AdminLoginProps) {
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')

  function handleSubmit(event: FormEvent): void {
    event.preventDefault()
    onSubmit({ username, password })
  }

  return (
    // Deliberately nothing like the client sign-in (the shell's deep marine
    // with the logo above a pale card): a dark coral half saying in large type
    // that this is platform administration, the form on white beside it, and
    // a way out for a customer who landed here - so nobody types the
    // platform-wide credential into the wrong screen or mistakes this one for
    // their own dashboard's (the design chosen on 26 October 2026).
    <div className="min-h-screen flex flex-col md:flex-row">
      <section className="md:w-1/2 bg-[#A8372A] text-white px-6 py-10 md:p-16 flex flex-col justify-between gap-8">
        <div className="text-xs font-bold uppercase tracking-[3px]">autonoME</div>
        <div className="flex flex-col gap-6">
          <ShieldCheck aria-hidden="true" className="h-12 w-12 md:h-[72px] md:w-[72px]" strokeWidth={1.6} />
          <h1 className="text-4xl md:text-[56px] leading-[1.05] font-black">
            Platform
            <br />
            administration
          </h1>
        </div>
        <a href={CLIENT_SIGN_IN_PATH} className="text-[15px] font-bold underline text-white hover:text-coral-lt">
          Customer? Go to your dashboard sign-in →
        </a>
      </section>

      <section className="md:w-1/2 flex-1 bg-white flex items-center justify-center px-6 py-10 md:p-16">
        <form onSubmit={handleSubmit} className="w-full max-w-[420px] flex flex-col">
          <span className="self-start bg-coral-lt text-[#A8372A] text-xs font-extrabold uppercase tracking-[2px] px-3 py-1.5 mb-8">
            Admin sign-in
          </span>

          <label className="block text-sm font-bold text-ink mb-1.5" htmlFor="admin-username">
            Admin username
          </label>
          <input
            id="admin-username"
            className={INPUT_CLASS}
            value={username}
            onChange={(event) => setUsername(event.target.value)}
            autoComplete="username"
            autoFocus
            required
          />

          <label className="block text-sm font-bold text-ink mb-1.5" htmlFor="admin-password">
            Password
          </label>
          <PasswordInput
            id="admin-password"
            className={`${INPUT_CLASS} mb-7`}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete="current-password"
            required
          />

          {error && <div className="text-coral text-sm font-bold mb-4">{error}</div>}

          <Button type="submit" icon={LogIn} size="lg" fullWidth busy={isVerifying} className="py-4 text-[17px]">
            {isVerifying ? 'Signing in…' : 'Sign in'}
          </Button>

          <div className="flex justify-between gap-4 mt-5 text-[13px] text-ink-mid">
            <span>Reloading asks again.</span>
            <a href="/admin/forgot-password" className="font-bold text-marine hover:text-marine-dk">
              Forgot your password?
            </a>
          </div>
        </form>
      </section>
    </div>
  )
}
